import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const TenantEditModal = ({ tenant, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    seatsTaken: 0,
    monthlyRevenue: 0,
    lockinEndDate: '',
    riskLevel: 'low',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant?.name || '',
        email: tenant?.email || '',
        phone: tenant?.phone || '',
        company: tenant?.company || '',
        seatsTaken: tenant?.seatsTaken || 0,
        monthlyRevenue: tenant?.monthlyRevenue || 0,
        lockinEndDate: tenant?.lockinEndDate || '',
        riskLevel: tenant?.riskLevel || 'low',
        notes: tenant?.notes || ''
      });
    }
  }, [tenant]);

  const riskLevelOptions = [
    { value: 'low', label: 'Low Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'high', label: 'High Risk' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData?.company?.trim()) {
      newErrors.company = 'Company is required';
    }
    
    if (formData?.seatsTaken <= 0) {
      newErrors.seatsTaken = 'Seats must be greater than 0';
    }
    
    if (formData?.monthlyRevenue <= 0) {
      newErrors.monthlyRevenue = 'Monthly revenue must be greater than 0';
    }
    
    if (!formData?.lockinEndDate) {
      newErrors.lockinEndDate = 'Lock-in end date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...tenant,
        ...formData,
        seatsTaken: parseInt(formData?.seatsTaken),
        monthlyRevenue: parseFloat(formData?.monthlyRevenue)
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Tenant
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Full Name"
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
              placeholder="Enter tenant name"
            />

            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
              placeholder="Enter email address"
            />

            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              placeholder="Enter phone number"
            />

            <Input
              label="Company"
              type="text"
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
              error={errors?.company}
              required
              placeholder="Enter company name"
            />

            <Input
              label="Seats Occupied"
              type="number"
              value={formData?.seatsTaken}
              onChange={(e) => handleInputChange('seatsTaken', e?.target?.value)}
              error={errors?.seatsTaken}
              required
              min="1"
              placeholder="Number of seats"
            />

            <Input
              label="Monthly Revenue ($)"
              type="number"
              value={formData?.monthlyRevenue}
              onChange={(e) => handleInputChange('monthlyRevenue', e?.target?.value)}
              error={errors?.monthlyRevenue}
              required
              min="0"
              step="0.01"
              placeholder="Monthly revenue amount"
            />

            <Input
              label="Lock-in End Date"
              type="date"
              value={formData?.lockinEndDate}
              onChange={(e) => handleInputChange('lockinEndDate', e?.target?.value)}
              error={errors?.lockinEndDate}
              required
            />

            <Select
              label="Risk Level"
              options={riskLevelOptions}
              value={formData?.riskLevel}
              onChange={(value) => handleInputChange('riskLevel', value)}
              required
            />
          </div>

          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about the tenant..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantEditModal;